import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { BehaviorSubject, Observable, throwError } from "rxjs";
import { catchError, filter, switchMap, take, timeout } from "rxjs/operators";

// QSL-10608: hard ceiling on the refresh call. If the refresh request itself
// stalls (socket open, no response), `isRefreshing` would otherwise latch true
// forever and every subsequent request would hang behind it ("One moment…"
// forever). On timeout we fail the refresh, which drains the parked queue.
// QSL-10608b: 30s (was 20s) so a legitimately slow refresh (cold Cloud Run,
// slow mobile network) doesn't hit the failure path and log the user out
// spuriously; still bounded so a truly hung refresh can't latch forever.
const REFRESH_TIMEOUT_MS = 30000;

import * as fromStore from "../store";
import * as fromActions from "../store/actions";
import { AuthService } from "./auth.service";

@Injectable({ providedIn: "root" })
export class TokenInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private accessTokenSubject: BehaviorSubject<string> =
    new BehaviorSubject<string>(null);

  constructor(
    public auth: AuthService,
    private store: Store<fromStore.IRootState>
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // QSL-10608: NEVER route the refresh call back through the 401→refresh logic.
    // AuthService.refreshToken() uses HttpClient, which re-enters this interceptor;
    // if that PUT 401s it would recurse into handle401Error while isRefreshing is
    // already true → park on a subject that never emits → permanent deadlock that
    // hangs the whole app. Pass it straight through.
    if (request.url.includes("/refresh-token")) {
      return next.handle(request);
    }

    const accessToken = this.auth.getAccessToken();

    if (accessToken) {
      request = this.addToken(request, accessToken);
    }

    return next.handle(request).pipe(
      catchError((err) => {
        if (err instanceof HttpErrorResponse && err.status === 401) {
          // In mobile WebView: no refresh token available, switch to guest mode
          // so Siraj continues working with guest endpoints instead of white-screening.
          if ((window as any).QUPIL_MOBILE && !this.auth.getRefreshToken()) {
            (window as any).QUPIL_AUTH_TOKEN = null;
            (window as any).QUPIL_GUEST_MODE = true;
            return throwError(err);
          }

          if (this.auth.getRefreshToken()) {
            return this.handle401Error(request, next);
          }
        }
        return throwError(err);
      })
    );
  }

  private addToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
    //

    return request.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
  }

  /**
   * Handle refresh token
   *
   * @private
   * @param {HttpRequest<any>} request
   * @param {HttpHandler} next
   * @returns {Observable<HttpEvent<any>>}
   * @memberof TokenInterceptor
   */
  private handle401Error(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.accessTokenSubject.next(null);

      return this.auth.refreshToken().pipe(
        timeout(REFRESH_TIMEOUT_MS),
        switchMap(({ accessToken }) => {
          this.isRefreshing = false;
          this.accessTokenSubject.next(accessToken);
          return next.handle(this.addToken(request, accessToken));
        }),
        catchError((err) => {
          this.isRefreshing = false;
          // QSL-10608: unblock every request parked in the else-branch below. They
          // are waiting on `filter(token != null)`, which a failed refresh would
          // never satisfy → they would hang forever. Error the current subject
          // (propagates to parked subscribers so their requests fail fast) and
          // replace it with a fresh one for the next refresh cycle.
          const parked = this.accessTokenSubject;
          this.accessTokenSubject = new BehaviorSubject<string>(null);
          parked.error(err);

          // In WebView: don't dispatch logout (which navigates to /auth → white screen).
          // Instead, fall back to guest mode so Siraj continues on guest endpoints.
          if ((window as any).QUPIL_MOBILE) {
            (window as any).QUPIL_AUTH_TOKEN = null;
            (window as any).QUPIL_REFRESH_TOKEN = null;
            (window as any).QUPIL_GUEST_MODE = true;
            return throwError(err);
          }
          this.store.dispatch(fromActions.logoutOrganizationSuccess());
          return throwError(err);
        })
      );
    } else {
      return this.accessTokenSubject.pipe(
        filter((token) => token != null),
        take(1),
        switchMap((accessToken) => {
          return next.handle(this.addToken(request, accessToken));
        })
      );
    }
  }
}
